import { useCallback, useEffect, useRef } from 'react';

import { ChakraFlex, ChakraGrid } from '@equip.health/ui';
import { useDispatch } from 'react-redux';

import {
  DashboardV2EventProps,
  DashboardV2Events,
  DashboardV2ViewingMode,
} from '~/analytics/dashboardV2Events';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  ProviderSupervisorProvider,
  useProviderSupervisor,
} from '~/lib/contexts/ProviderSupervisorContext';
import api from '~/store/services/api';
import { CacheTag } from '~/store/services/CacheTag';
import { useGetAttendedPatientsCountQuery } from '~/store/services/providerDashboard';

import ProviderDashboardAlertsCard from './alerts/ProviderDashboardAlertsCard';
import { DASHBOARD_V2_GRID } from './constants';
import ProviderDashboardGreetingHeader from './ProviderDashboardGreetingHeader';
import './ProviderDashboard.css';

const ProviderDashboardV2Content = () => {
  const { viewingProvider, viewingProviderId, isSelf, isLoadingSupervisees } =
    useProviderSupervisor();
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics() ?? {};

  const {
    data: attendedPatientsCountData,
    isLoading: isLoadingAttendedPatients,
    isFetching: isFetchingAttendedPatients,
    isError: isErrorAttendedPatients,
  } = useGetAttendedPatientsCountQuery(
    { providerId: viewingProviderId },
    { skip: isLoadingSupervisees },
  );

  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current || isLoadingSupervisees) return;
    hasTrackedView.current = true;
    trackEvent?.(DashboardV2Events.dashboardViewed, {
      [DashboardV2EventProps.ViewingMode]: isSelf
        ? DashboardV2ViewingMode.SELF
        : DashboardV2ViewingMode.SUPERVISOR,
    });
  }, [isLoadingSupervisees, isSelf, trackEvent]);

  const handleRefresh = useCallback(() => {
    dispatch(
      api.util.invalidateTags([
        CacheTag.ProviderDashboard,
        CacheTag.ProviderDashboardV2,
      ]),
    );
  }, [dispatch]);

  const displayName =
    viewingProvider.displayFirstName || viewingProvider.firstName || '';

  return (
    <ChakraFlex
      data-testid="provider-dashboard-v2"
      direction="column"
      height="100%"
      overflowY="auto"
      width="100%"
    >
      <ChakraFlex
        direction="column"
        gap={DASHBOARD_V2_GRID.gap}
        marginX="auto"
        padding="28px 40px"
        maxWidth="1180px"
        width="100%"
      >
        <ProviderDashboardGreetingHeader
          displayName={displayName}
          distinctPatientsCount={attendedPatientsCountData?.count}
          isLoadingPatientsCount={
            isLoadingSupervisees ||
            isLoadingAttendedPatients ||
            isFetchingAttendedPatients
          }
          isErrorPatientsCount={isErrorAttendedPatients}
          onRefresh={handleRefresh}
          isRefreshing={isFetchingAttendedPatients}
        />

        <ChakraGrid
          templateColumns={DASHBOARD_V2_GRID.templateColumns}
          gap={DASHBOARD_V2_GRID.gap}
          alignItems="start"
        >
          <ChakraFlex
            data-testid="dashboard-v2-left-column"
            direction="column"
            gap={DASHBOARD_V2_GRID.gap}
            minWidth="0"
          >
            <ProviderDashboardAlertsCard />
          </ChakraFlex>

          <ChakraFlex
            data-testid="dashboard-v2-right-column"
            direction="column"
            gap={DASHBOARD_V2_GRID.gap}
            minWidth="0"
          />
        </ChakraGrid>
      </ChakraFlex>
    </ChakraFlex>
  );
};

const ProviderDashboardV2 = () => (
  <ProviderSupervisorProvider>
    <ProviderDashboardV2Content />
  </ProviderSupervisorProvider>
);

export default ProviderDashboardV2;
